import moment from "moment";

const isoDateToMoment = (isoDate: moment.MomentInput) =>
  moment(isoDate, "YYYY-MM-DD");

export const formatRelease = (
  isoDate: moment.MomentInput,
  format: string = "MMMM YYYY"
) => isoDateToMoment(isoDate).format(format);

/**
 * The quarter a release *reports on*, which is not the quarter it is dated in.
 *
 * Both changelogs state the rule: "a release dated `YYYY-MM-01` reports on the
 * quarter that has just ended", so `2023-10-01` covers July-September 2023 and
 * `2026-07-01` covers April-June 2026.
 *
 * Implemented as "the quarter containing the day before the release date"
 * rather than "one quarter earlier". Both agree on every quarter-aligned
 * release, but this one also gets the mid-quarter `2023-06-01` baseline right:
 * it reports up to 31 May 2023, which is Q2 2023.
 */
const reportedQuarter = (release: moment.MomentInput) =>
  isoDateToMoment(release).subtract(1, "day");

/** "2023-10-01" -> "Q3 2023". Note the January trap: "2024-01-01" -> "Q4 2023". */
export const toQuarter = (release: moment.MomentInput) =>
  reportedQuarter(release).format("[Q]Q YYYY");

/** Short quarter label without the year: "2023-10-01" -> "Q3". */
export const toQuarterShort = (release: moment.MomentInput) =>
  reportedQuarter(release).format("[Q]Q");

/**
 * Year of the reported quarter, used for grouping.
 * `2024-01-01` belongs to **2023**, since it reports on Q4 2023.
 */
export const releaseYear = (release: moment.MomentInput) =>
  reportedQuarter(release).format("YYYY");

/** First day of the reported quarter, as `{ year, month, day }` (month is 1-12). */
export const reportedQuarterStart = (release: moment.MomentInput) => {
  const start = reportedQuarter(release).startOf("quarter");
  return {
    year: start.year(),
    month: start.month() + 1,
    day: start.date(),
  };
};
